import type { FC } from "react";
import { useDraggable } from "@dnd-kit/core";
import { HiLocationMarker } from "react-icons/hi";
import { IOrder } from "../../types";
import { StatusBadge } from "@components";
import { Customer } from "../../models";
import { cn, cardDraggable, textHeading, Stack, Inline } from "@design-system";

interface DraggableOrderProps {
  order: IOrder;
  isDraggable: boolean;
}

export const DraggableOrder: FC<DraggableOrderProps> = ({
  order,
  isDraggable,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: order.id,
    disabled: !isDraggable,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  // Format the first pickup and last delivery times/locations for the tooltip
  const firstStop = order.route?.[0];
  const lastStop = order.route?.[order.route.length - 1];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isDraggable ? listeners : {})}
      {...(isDraggable ? attributes : {})}
      className={cn(
        cardDraggable,
        "p-2.5 touch-none select-none",
        isDraggable ? "cursor-move" : "cursor-default opacity-80",
      )}
    >
      <Stack gap={1.5} className="w-full">
        <Inline gap={2} justify="between" className="w-full">
          <div className="flex-1 min-w-0">
            <h5 className={`text-sm font-medium tracking-tight ${textHeading} truncate`}>
              {Customer.getCustomerName(order.customer)}
            </h5>
          </div>
          <StatusBadge status={order.status} rounded />
        </Inline>

        <div className="space-y-1">
          {/* Pickup address */}
          <Inline gap={1} className="text-xs text-muted">
            <HiLocationMarker className="h-3 w-3 text-brand" />
            <span className="truncate">
              {firstStop?.location?.addressLine ||
                firstStop?.location?.city ||
                "Ingen hentested"}
            </span>
          </Inline>

          {/* Delivery address */}
          <Inline gap={1} className="text-xs text-muted">
            <HiLocationMarker className="h-3 w-3 text-success" />
            <span className="truncate">
              {lastStop?.location?.addressLine ||
                lastStop?.location?.city ||
                "Ingen leveringssted"}
            </span>
          </Inline>
        </div>
      </Stack>

      {/* Order number in bottom right corner */}
      {order.orderNumber && (
        <span className="absolute bottom-2 right-2 text-xs text-muted font-mono">
          #{order.orderNumber}
        </span>
      )}
    </div>
  );
};
